import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, interval, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DesktopAppService } from '@intranet/core/services/desktop-app.service';
import { TimerSyncService } from '@intranet/core/services/timer-sync.service';

/** Public state for the offline overlay.
 *  - online:  the app is reachable, normal operation.
 *  - offline: the app cannot reach the server. Overlay shown to block
 *             input until the connection comes back, so the user doesn't
 *             make changes that will be silently lost. */
export type ConnectivityState = 'online' | 'offline';

/**
 * Tracks whether the app can actually reach the Intranet. Combines two
 * signals:
 *
 *   1. `online` / `offline` browser events — fast, OS-level signal when
 *      the network adapter goes up or down. Triggers an immediate check
 *      rather than being trusted on its own (the adapter can report
 *      "online" while real internet is down — captive portal, DNS broken,
 *      VPN dropped, etc.).
 *
 *   2. A lightweight heartbeat that fetches `/assets/version.json` (the
 *      same small file the build-version service already uses) every 15
 *      seconds. Two consecutive failures flip the state to offline; one
 *      success instantly flips it back to online. The two-failure
 *      threshold avoids flicker on transient sub-second blips.
 *
 * Only runs inside the Electron desktop shell — the browser intranet has
 * no overlay to drive, so we don't want to add background polling there.
 *
 * On the offline → online transition, the timer-sync service is poked so
 * any data that changed server-side while the user was offline (e.g. they
 * stopped the timer from a browser tab) gets pulled in.
 */
@Injectable({ providedIn: 'root' })
export class ConnectivityService implements OnDestroy {
    private readonly _state$ = new BehaviorSubject<ConnectivityState>('online');
    readonly state$: Observable<ConnectivityState> = this._state$.asObservable();

    private heartbeatSub: Subscription | null = null;
    private onlineHandler: (() => void) | null = null;
    private offlineHandler: (() => void) | null = null;
    private inflightCheck = false;

    private consecutiveFailures = 0;
    private consecutiveSuccesses = 0;
    /** Two consecutive heartbeat failures before flipping to offline.
     *  Stops a single 1s blip from showing an overlay. */
    private readonly FAILURE_THRESHOLD = 2;
    /** Same threshold in reverse: two consecutive successes before flipping
     *  back to online. Without it, a single response served from Chromium's
     *  disk cache (which can happen during the offline fallback) would
     *  immediately dismiss the overlay while the user is still offline. */
    private readonly SUCCESS_THRESHOLD = 2;
    private readonly HEARTBEAT_MS = 15_000;
    /** Reuse the version.json file the build-version service already
     *  serves — small (~80 bytes), always present, no extra endpoint
     *  needed. Cache-busted on every request. */
    private readonly URL = 'assets/version.json';
    /** Request headers that defeat Chromium's offline cache fallback, so
     *  the heartbeat genuinely reflects whether the server is reachable
     *  rather than whether we happen to have a cached copy. */
    private readonly NO_CACHE_HEADERS = new HttpHeaders({
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
    });

    constructor(
        private http: HttpClient,
        private desktopApp: DesktopAppService,
        private timerSyncService: TimerSyncService,
    ) {
        // Electron-only — the overlay isn't mounted in the regular
        // browser intranet, so running the heartbeat there would just be
        // background noise without any user-visible benefit.
        if (this.desktopApp.isTimerApp) {
            this.start();
        }
    }

    private start(): void {
        // OS-level network change signals — fast, but treated only as a
        // hint to re-check rather than trusted directly.
        this.onlineHandler = () => this.doCheck();
        this.offlineHandler = () => {
            // OS says we're offline — skip the threshold and flip
            // immediately so the overlay appears right away. Reset the
            // success counter too so any past successes don't count
            // toward the eventual recovery.
            this.consecutiveFailures = this.FAILURE_THRESHOLD;
            this.consecutiveSuccesses = 0;
            this.flip('offline');
        };
        window.addEventListener('online', this.onlineHandler);
        window.addEventListener('offline', this.offlineHandler);

        this.heartbeatSub = interval(this.HEARTBEAT_MS).subscribe(() => this.doCheck());

        // First check happens immediately at boot rather than waiting for
        // the first 15s tick.
        this.doCheck();
    }

    private doCheck(): void {
        // Prevent overlapping checks if a slow request hasn't returned
        // yet by the time the next tick fires.
        if (this.inflightCheck) return;
        this.inflightCheck = true;
        this.http.get(`${this.URL}?t=${Date.now()}`, {
            responseType: 'json' as const,
            headers: this.NO_CACHE_HEADERS,
        })
            .pipe(catchError(() => of(null)))
            .subscribe(result => {
                this.inflightCheck = false;
                if (result !== null) {
                    // Success — reset failure streak. Only flip to online
                    // after SUCCESS_THRESHOLD consecutive successes when
                    // currently offline (defends against a stray cached
                    // response prematurely dismissing the overlay).
                    this.consecutiveFailures = 0;
                    if (this._state$.value === 'offline') {
                        this.consecutiveSuccesses++;
                        if (this.consecutiveSuccesses >= this.SUCCESS_THRESHOLD) {
                            this.flip('online');
                        }
                    }
                } else {
                    // Failure — reset success streak. Only flip to offline
                    // after FAILURE_THRESHOLD consecutive failures when
                    // currently online.
                    this.consecutiveSuccesses = 0;
                    if (this._state$.value === 'online') {
                        this.consecutiveFailures++;
                        if (this.consecutiveFailures >= this.FAILURE_THRESHOLD) {
                            this.flip('offline');
                        }
                    }
                }
            });
    }

    private flip(newState: ConnectivityState): void {
        const previousState = this._state$.value;
        if (previousState === newState) return;
        this._state$.next(newState);
        console.log(`[connectivity] state changed: ${previousState} → ${newState}`);
        // Reset both streaks on every transition so the next direction's
        // threshold starts from zero.
        this.consecutiveFailures = 0;
        this.consecutiveSuccesses = 0;
        // Reconnect sync — poke the timer-sync service so the
        // timer-tracker and header reload data the user couldn't see
        // change during the offline window.
        if (previousState === 'offline' && newState === 'online') {
            this.timerSyncService.notify('entries-changed');
        }
    }

    ngOnDestroy(): void {
        if (this.heartbeatSub) {
            this.heartbeatSub.unsubscribe();
            this.heartbeatSub = null;
        }
        if (this.onlineHandler) {
            window.removeEventListener('online', this.onlineHandler);
            this.onlineHandler = null;
        }
        if (this.offlineHandler) {
            window.removeEventListener('offline', this.offlineHandler);
            this.offlineHandler = null;
        }
    }
}
